/**
 * Customer-facing affiliate / "Refer & Earn" page.
 *
 * Self-serve activation: clicking "Become an affiliate" calls
 * POST /api/affiliate/enable which lazily creates the affiliate row and
 * returns a unique referral code + shareable signup link. Re-opening the
 * page hits GET /api/affiliate/me which is idempotent — once enabled the
 * user is shown their existing code + stats.
 *
 * Reward model (2026-10-03): 100 enrichment credits to BOTH the affiliate
 * and the referred user when the new user makes their first real payment.
 * Signup only records the referral (/api/affiliate/attribute); the credits
 * are granted by the Stripe webhook. See features/affiliate/capture-referral.ts.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Gift,
  Loader2,
  Copy,
  Check,
  Users as UsersIcon,
  Sparkles,
  Share2,
  AlertCircle,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { PageHeader } from '@/components/layout/page-header'
import { useApi } from '@/hooks/use-api'
import { fmtLocale } from '@/lib/dates'

type AffiliateMe =
  | {
      readonly enabled: false
      readonly reward_per_referral: number
      readonly signup_bonus_for_referred: number
    }
  | {
      readonly enabled: true
      readonly code: string
      readonly share_link: string
      readonly total_referrals: number
      readonly total_credits_awarded: number
      readonly created_at: string
      readonly reward_per_referral: number
      readonly signup_bonus_for_referred: number
    }

type Referral = {
  readonly id: string
  readonly referred_user_id: string
  readonly credits_awarded_to_referrer: number
  readonly status: 'signed_up' | 'paid' | 'churned'
  readonly referred_at: string
}

const AFFILIATE_QUERY_KEY = ['affiliate', 'me'] as const
const REFERRALS_QUERY_KEY = ['affiliate', 'referrals'] as const

export default function AffiliatePage() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)

  const meQuery = useQuery({
    queryKey: AFFILIATE_QUERY_KEY,
    queryFn: async (): Promise<AffiliateMe> => {
      const res = await apiFetch<AffiliateMe>('/api/affiliate/me')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load')
      return res.data
    },
    staleTime: 30_000,
  })

  const referralsQuery = useQuery({
    queryKey: REFERRALS_QUERY_KEY,
    queryFn: async (): Promise<readonly Referral[]> => {
      const res = await apiFetch<readonly Referral[]>('/api/affiliate/referrals')
      if (!res.success || !res.data) return []
      return res.data
    },
    enabled: meQuery.data?.enabled === true,
    staleTime: 30_000,
  })

  const enableMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch<{ code: string; share_link: string }>(
        '/api/affiliate/enable',
        { method: 'POST', body: {} },
      )
      if (!res.success) throw new Error(res.error ?? 'Activation failed')
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AFFILIATE_QUERY_KEY })
    },
  })

  function copyLink(text: string) {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    })
  }

  if (meQuery.isLoading) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Refer & Earn" subtitle="Share DealCloser, earn credits." />
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  if (meQuery.isError || !meQuery.data) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Refer & Earn" subtitle="Share DealCloser, earn credits." />
        <Card>
          <CardContent className="flex items-center gap-3 py-6 text-sm text-destructive">
            <AlertCircle className="h-5 w-5" />
            Couldn't load your affiliate state. Refresh and try again.
          </CardContent>
        </Card>
      </div>
    )
  }

  const me = meQuery.data

  // Not yet enabled — show the activation card.
  if (!me.enabled) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Refer & Earn" subtitle="Share DealCloser, earn credits." />
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Gift className="h-5 w-5 text-cobalt-bright" />
              </div>
              <div>
                <CardTitle>Join the affiliate program</CardTitle>
                <CardDescription>
                  Share a personal link. Both you and your friend get {me.reward_per_referral} enrichment credits when they make their first payment.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <RewardChip
                icon={<Sparkles className="h-4 w-4" />}
                label="You get"
                value={`${me.reward_per_referral} credits`}
                detail="per friend who subscribes"
              />
              <RewardChip
                icon={<Gift className="h-4 w-4" />}
                label="They get"
                value={`${me.signup_bonus_for_referred} credits`}
                detail="as a welcome bonus"
              />
              <RewardChip
                icon={<UsersIcon className="h-4 w-4" />}
                label="Monthly limit"
                value="5 rewards"
                detail="per month, unlimited referrals"
              />
            </div>
            <Separator />
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => enableMutation.mutate()}
                disabled={enableMutation.isPending}
                className="w-full sm:w-auto"
              >
                {enableMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating your link...
                  </>
                ) : (
                  <>
                    <Share2 className="mr-2 h-4 w-4" />
                    Generate my referral link
                  </>
                )}
              </Button>
              {enableMutation.isError && (
                <p className="text-sm text-destructive">
                  {enableMutation.error instanceof Error
                    ? enableMutation.error.message
                    : 'Activation failed. Try again.'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Enabled — show the share card + referrals list.
  const referrals = referralsQuery.data ?? []

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Refer & Earn" subtitle="Share DealCloser, earn credits." />

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Share2 className="h-5 w-5 text-cobalt-bright" />
            </div>
            <div>
              <CardTitle>Your referral link</CardTitle>
              <CardDescription>
                Share this anywhere. Both you and your friend get {me.reward_per_referral} credits when they make their first payment.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              readOnly
              value={me.share_link}
              className="flex-1 font-mono text-sm"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button
              variant="default"
              onClick={() => copyLink(me.share_link)}
              className="shrink-0"
            >
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy link
                </>
              )}
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            Your code:{' '}
            <code className="rounded bg-muted px-2 py-1 font-mono">{me.code}</code>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Total referrals"
          value={String(me.total_referrals)}
          icon={<UsersIcon className="h-5 w-5 text-cobalt-bright" />}
        />
        <StatCard
          label="Credits earned"
          value={String(me.total_credits_awarded)}
          icon={<Sparkles className="h-5 w-5 text-cobalt-bright" />}
        />
        <StatCard
          label="Per referral"
          value={`${me.reward_per_referral} credits`}
          icon={<Gift className="h-5 w-5 text-cobalt-bright" />}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your referrals</CardTitle>
          <CardDescription>
            Each row is a person who signed up using your link. Credits are added once they make their first payment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {referralsQuery.isLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : referrals.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              No referrals yet. Share your link to start earning.
            </div>
          ) : (
            <div className="divide-y">
              {referrals.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between py-3"
                >
                  <div className="flex flex-col">
                    <span className="font-mono text-xs text-muted-foreground">
                      {r.referred_user_id}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {fmtLocale(r.referred_at, 'date')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">+{r.credits_awarded_to_referrer} credits</Badge>
                    <Badge variant="outline" className="capitalize">
                      {r.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function RewardChip({
  icon,
  label,
  value,
  detail,
}: {
  readonly icon: React.ReactNode
  readonly label: string
  readonly value: string
  readonly detail: string
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{detail}</div>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
}: {
  readonly label: string
  readonly value: string
  readonly icon: React.ReactNode
}) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between py-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="text-2xl font-semibold">{value}</div>
        </div>
        {icon}
      </CardContent>
    </Card>
  )
}
